import React, { useEffect, useState } from 'react';
import api from '../../utils/axios';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Eye, FileDown, Loader2 } from 'lucide-react';
import { BuscarTitular } from './BuscarTitular';
import { FiltrosPedido } from './FiltrosPedido';
import { MotivoFormatos } from './MotivoFormatos';
import { VistaPrevia } from './VistaPrevia';
import { ATAJOS } from './formato';

const Paso = ({ n, titulo, children, testid }) => (
  <Card className="p-4 sm:p-5 border-slate-200 dark:border-slate-800" data-testid={testid}>
    <div className="flex items-center gap-2 mb-3">
      <span className="h-6 w-6 rounded-full bg-blue-700 text-white text-xs font-bold flex items-center justify-center">{n}</span>
      <h2 className="text-base font-semibold text-slate-900 dark:text-white">{titulo}</h2>
    </div>
    {children}
  </Card>
);

const numOrNull = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

export const NuevoPedido = ({ config, onCreado }) => {
  const [titular, setTitular] = useState(null);
  const [seleccion, setSeleccion] = useState([]);
  const [filtros, setFiltros] = useState(() => {
    const [desde, hasta] = ATAJOS.find((a) => a.key === '3m').rango();
    return { desde, hasta, sentido: 'TODOS', monto_min: '', monto_max: '', contraparte: '' };
  });
  const [datos, setDatos] = useState({ motivo: null, referencia: '', comentario: '', formatos: ['XLSX', 'PDF'] });
  const [prev, setPrev] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [generando, setGenerando] = useState(false);

  useEffect(() => { setPrev(null); }, [filtros, seleccion]);

  const body = () => ({
    cvus: seleccion, desde: filtros.desde, hasta: filtros.hasta, sentido: filtros.sentido,
    monto_min: numOrNull(filtros.monto_min), monto_max: numOrNull(filtros.monto_max),
    contraparte: filtros.contraparte.trim() || null,
  });

  const verPrevia = async () => {
    setCargando(true);
    try {
      setPrev((await api.post('/export-movimientos/preview', body())).data);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'No se pudo calcular la vista previa');
    } finally { setCargando(false); }
  };

  const generar = async () => {
    setGenerando(true);
    try {
      const r = await api.post('/export-movimientos/pedidos', {
        ...body(), objetivo: titular.objetivo, tipo_objetivo: titular.tipo, cuit: titular.cuit, titular: titular.titular,
        motivo: datos.motivo, referencia: datos.referencia || null, comentario: datos.comentario || null, formatos: datos.formatos,
      });
      toast.success(`Pedido ${r.data.numero} creado. Lo estamos generando.`);
      onCreado(r.data.id);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'No se pudo crear el pedido');
    } finally { setGenerando(false); }
  };

  const listoParaPrevia = titular && seleccion.length > 0 && filtros.desde && filtros.hasta;
  const puedeGenerar = prev && prev.cantidad > 0 && !prev.supera_maximo && datos.formatos.length > 0;

  return (
    <div className="space-y-4" data-testid="expmov-nuevo">
      <Paso n={1} titulo="¿De quién?" testid="expmov-paso-titular">
        <BuscarTitular titular={titular} setTitular={setTitular} seleccion={seleccion} setSeleccion={setSeleccion} />
      </Paso>
      <Paso n={2} titulo="Período y filtros" testid="expmov-paso-filtros">
        <FiltrosPedido filtros={filtros} setFiltros={setFiltros} />
        {!config.historica_conectada && filtros.desde < config.corte && (
          <p className="text-xs text-amber-700 mt-3" data-testid="expmov-aviso-historica">
            La base con los movimientos anteriores al 01/01/2026 todavía no está conectada: ese tramo no se va a incluir.
          </p>
        )}
      </Paso>
      <Paso n={3} titulo="Motivo y formato" testid="expmov-paso-motivo">
        <MotivoFormatos motivos={config.motivos} datos={datos} setDatos={setDatos} />
      </Paso>
      <Paso n={4} titulo="Vista previa y exportación" testid="expmov-paso-preview">
        <div className="flex flex-wrap gap-2 mb-3">
          <Button variant="outline" onClick={verPrevia} disabled={!listoParaPrevia || cargando} data-testid="expmov-preview-btn">
            {cargando ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Eye className="h-4 w-4 mr-1.5" />} Ver vista previa
          </Button>
          <Button onClick={generar} disabled={!puedeGenerar || generando} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="expmov-generar-btn">
            {generando ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileDown className="h-4 w-4 mr-1.5" />} Generar exportación
          </Button>
        </div>
        {!listoParaPrevia && <p className="text-sm text-slate-500">Buscá un titular y elegí al menos una cuenta para ver la vista previa.</p>}
        {prev && <VistaPrevia prev={prev} />}
        {prev && prev.cantidad === 0 && <p className="text-sm text-slate-500 mt-2" data-testid="expmov-sin-movs">No hay movimientos con esos filtros.</p>}
      </Paso>
    </div>
  );
};

export default NuevoPedido;
